import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useAuth0Context } from './contexts/Auth0Context';
import { useTheme } from './contexts/ThemeContext';
import { PROCOGIA_COLORS } from './utils/themeUtils';
import LoginPage from './pages/LoginPage';
import SocialLoginPage from './components/SocialLoginPage';
import ProfileSelector from './components/ProfileSelector';

import Layout from './components/Layout';
import OperationsManagerDashboard from './pages/OperationsManagerDashboard';
import MaintenanceEngineerDashboard from './pages/MaintenanceEngineerDashboard';
import PlantSupervisorDashboard from './pages/PlantSupervisorDashboard';
import AdminDashboard from './pages/AdminDashboard';
import EquipmentDetail from './pages/EquipmentDetail';
import EquipmentListPage from './pages/EquipmentListPage';
import AlertsPage from './pages/AlertsPage';
import LoadingSpinner from './components/LoadingSpinner';
import APITester from './components/APITester';
// New dashboard imports
import OverviewDashboard from './pages/OverviewDashboard';
import UsersDashboard from './pages/UsersDashboard';
import EquipmentDashboard from './pages/EquipmentDashboard';
import SystemTestingDashboard from './pages/SystemTestingDashboard';
import ChatPage from './pages/ChatPage';

// Callback handler component with redirect logic
const CallbackHandler = () => {
  const { auth0IsLoading, auth0IsAuthenticated, auth0Error, isAuthenticated } = useAuth0Context();
  const navigate = useNavigate();
  const { getThemeClasses } = useTheme();
  const themeClasses = getThemeClasses();

  // Debug logging on every render
  console.log('🔍 CallbackHandler render:', {
    auth0IsLoading,
    auth0IsAuthenticated, 
    isAuthenticated,
    auth0Error: auth0Error?.message
  });

  useEffect(() => {
    console.log('🔍 CallbackHandler useEffect triggered:', {
      auth0IsLoading,
      auth0IsAuthenticated,
      isAuthenticated,
      auth0Error: auth0Error?.message
    });

    // Wait a bit for Auth0 to process, then redirect based on authentication state
    const redirectTimer = setTimeout(() => {
      if (!auth0IsLoading && auth0IsAuthenticated) {
        console.log('✅ Auth0 callback complete, redirecting to dashboard...');
        navigate('/dashboard', { replace: true });
      }
      else if (!auth0IsLoading && auth0Error) {
        console.log('❌ Auth0 callback error, redirecting to login...', auth0Error);
        navigate('/login', { replace: true });
      }
      else if (!auth0IsLoading && !auth0IsAuthenticated) {
        console.log('⚠️ Auth0 callback - not authenticated, redirecting to login...');
        navigate('/login', { replace: true });
      }
    }, 1000); // Give Auth0 1 second to process

    return () => clearTimeout(redirectTimer);
  }, [auth0IsLoading, auth0IsAuthenticated, auth0Error, navigate]);

  // Show loading while processing with debug info
  return (
    <div className="min-h-screen flex items-center justify-center app-background bg-gray-50">
      <div className="text-center">
        <LoadingSpinner size="lg" />
        <p className="mt-4 text-gray-600">Completing login...</p>
        <p className="mt-2 text-sm text-gray-500">
          {auth0IsLoading ? 'Processing authentication...' : 'Preparing redirect...'}
        </p>
        
        {/* Debug info in development */}
        <div className="mt-4 text-xs text-gray-400 space-y-1">
          <div>Loading: {auth0IsLoading ? '✓' : '✗'}</div>
          <div>Auth0 Authenticated: {auth0IsAuthenticated ? '✓' : '✗'}</div>
          <div>App Authenticated: {isAuthenticated ? '✓' : '✗'}</div>
          {auth0Error && <div className="text-red-400">Error: {auth0Error.message}</div>}
        </div>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, appUser, hasAnyRole } = useAuth0Context();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !hasAnyRole(allowedRoles)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

const DashboardRouter = () => {
  const { appUser } = useAuth0Context();

  switch (appUser?.role) {
    case 'operations_manager':
      return <OperationsManagerDashboard />;
    case 'maintenance_engineer':
      return <MaintenanceEngineerDashboard />;
    case 'plant_supervisor':
      return <PlantSupervisorDashboard />;
    case 'admin':
      return <AdminDashboard />;
    default:
      return <MaintenanceEngineerDashboard />;
  }
};

function App({ traditionalOnly = false }) {
  const {
    auth0IsLoading,
    auth0IsAuthenticated,
    isAuthenticated,
    showProfileSelector,
    auth0Error
  } = useAuth0Context();
  const { getThemeClasses } = useTheme();
  const themeClasses = getThemeClasses();

  // Add timeout for stuck loading states
  const [showTimeoutOption, setShowTimeoutOption] = useState(false);

  useEffect(() => {
    if (auth0IsLoading) {
      // Show timeout option after 15 seconds of loading
      const timer = setTimeout(() => {
        setShowTimeoutOption(true);
      }, 15000);

      return () => clearTimeout(timer);
    } else {
      setShowTimeoutOption(false);
    }
  }, [auth0IsLoading]);

  // Handle Auth0 errors and stuck states
  const handleAuth0Recovery = () => {
    console.log('🔧 Attempting Auth0 recovery...');
    
    // Clear Auth0 cache and state
    localStorage.removeItem('auth0.cache');
    localStorage.removeItem('auth0.is.authenticated');
    sessionStorage.clear();
    
    // Clear any profile selections
    const auth0Keys = Object.keys(localStorage).filter(key => key.startsWith('selectedProfile_'));
    auth0Keys.forEach(key => localStorage.removeItem(key));
    
    // Redirect to login
    window.location.href = '/login';
  };

  // Show loading spinner while Auth0 is initializing
  if (auth0IsLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center app-background bg-gray-50">
        <LoadingSpinner size="lg" />
        <p className="mt-4 text-gray-600">Completing login...</p>

        {showTimeoutOption && (
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500 mb-4">Taking longer than expected?</p>
            <div className="space-x-4">
              <button
                onClick={() => window.location.reload()}
                className="text-white px-4 py-2 rounded-lg text-sm transition-colors"
                style={{ backgroundColor: PROCOGIA_COLORS.green }}
                onMouseEnter={(e) => e.target.style.backgroundColor = PROCOGIA_COLORS.blue}
                onMouseLeave={(e) => e.target.style.backgroundColor = PROCOGIA_COLORS.green}
              >
                Refresh Page
              </button>
              <button
                onClick={() => window.location.href = '/login/traditional'}
                className="text-white px-4 py-2 rounded-lg text-sm transition-colors"
                style={{ backgroundColor: PROCOGIA_COLORS.darkGrey }}
                onMouseEnter={(e) => e.target.style.backgroundColor = PROCOGIA_COLORS.lightGrey}
                onMouseLeave={(e) => e.target.style.backgroundColor = PROCOGIA_COLORS.darkGrey}
              >
                Try Email Login
              </button>
              <button
                onClick={handleAuth0Recovery}
                className="text-white px-4 py-2 rounded-lg text-sm transition-colors"
                style={{ backgroundColor: PROCOGIA_COLORS.blue }}
                onMouseEnter={(e) => e.target.style.backgroundColor = PROCOGIA_COLORS.green}
                onMouseLeave={(e) => e.target.style.backgroundColor = PROCOGIA_COLORS.blue}
              >
                Clear & Retry
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Show error page if Auth0 has an error
  if (auth0Error) {
    return (
      <div className={`min-h-screen flex items-center justify-center app-background ${themeClasses.bg.primary}`}>
        <div className="text-center max-w-md mx-auto p-8">
          <div className="text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Authentication Error</h1>
          <p className="text-gray-600 mb-4">{auth0Error.message}</p>
          <p className="text-sm text-gray-500 mb-6">
            This usually happens when using the browser back button during login. 
            Please use the "Switch Role" button instead.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="w-full text-white font-semibold py-2 px-4 rounded-lg transition-colors"
              style={{ backgroundColor: PROCOGIA_COLORS.green }}
              onMouseEnter={(e) => e.target.style.backgroundColor = PROCOGIA_COLORS.blue}
              onMouseLeave={(e) => e.target.style.backgroundColor = PROCOGIA_COLORS.green}
            >
              Reload Page
            </button>
            <button
              onClick={handleAuth0Recovery}
              className="w-full text-white font-semibold py-2 px-4 rounded-lg transition-colors"
              style={{ backgroundColor: PROCOGIA_COLORS.darkGrey }}
              onMouseEnter={(e) => e.target.style.backgroundColor = PROCOGIA_COLORS.lightGrey}
              onMouseLeave={(e) => e.target.style.backgroundColor = PROCOGIA_COLORS.darkGrey}
            >
              Clear Cache & Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show profile selector if user is authenticated but hasn't selected a profile
  if (auth0IsAuthenticated && showProfileSelector) {
    return <ProfileSelector />;
  }

  return (
    <div className={`min-h-screen app-background ${themeClasses.bg.primary}`}>
      <Routes>
        {/* Auth0 callback route - with redirect logic */}
        <Route path="/callback" element={<CallbackHandler />} />

        {/* Public Routes - Show Social Login (Auth0) at root when configured */}
        <Route
          path="/"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <SocialLoginPage />
          }
        />

        {/* Legacy login route for fallback */}
        <Route
          path="/login/traditional"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
          }
        />

        {/* Protected Dashboard Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <OverviewDashboard />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/equipment/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <EquipmentDetail />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/equipment"
          element={
            <ProtectedRoute>
              <Layout>
                <EquipmentListPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/alerts"
          element={
            <ProtectedRoute>
              <Layout>
                <AlertsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* New Dashboard Routes */}
        <Route
          path="/overview"
          element={
            <ProtectedRoute>
              <Layout>
                <OverviewDashboard />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/users"
          element={
            <ProtectedRoute allowedRoles={['admin', 'operations_manager']}>
              <Layout>
                <UsersDashboard />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/equipment-dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <EquipmentDashboard />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/chat"
          element={
            <ProtectedRoute>
              <Layout>
                <ChatPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/system-testing"
          element={
            <ProtectedRoute allowedRoles={['admin', 'operations_manager']}>
              <Layout>
                <SystemTestingDashboard />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* API Testing Route - Admin and Operations Manager only */}
        <Route
          path="/api-test"
          element={
            <ProtectedRoute allowedRoles={['admin', 'operations_manager']}>
              <Layout>
                <APITester />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Unauthorized page */}
        <Route
          path="/unauthorized"
          element={
            <div className={`min-h-screen flex items-center justify-center app-background ${themeClasses.bg.primary}`}>
              <div className="text-center">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">Access Denied</h1>
                <p className="text-gray-600 mb-6">
                  You don't have permission to access this page.
                </p>
                <button
                  onClick={() => window.history.back()}
                  className="text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                  style={{ backgroundColor: PROCOGIA_COLORS.green }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = PROCOGIA_COLORS.blue}
                  onMouseLeave={(e) => e.target.style.backgroundColor = PROCOGIA_COLORS.green}
                >
                  Go Back
                </button>
              </div>
            </div>
          }
        />

        {/* Catch all - redirect to appropriate page */}
        <Route
          path="*"
          element={<Navigate to={isAuthenticated ? "/dashboard" : "/"} replace />}
        />
      </Routes>
    </div>
  );
}

export default App; 